import { useEffect } from 'react'

import { clsx } from 'clsx'
import { Link, useLocation, useOutletContext } from 'react-router'

import { useDarkMode } from '~/components/DarkMode/hook'
import { useScrollIntoView } from '~/hooks/useScrollIntoView'
import { Operation } from '~/types/content'
import { RootOutletContext } from '~/types/portal'
import { buildOperationHash } from '~/utils/api'
import { getOperationColor } from '~/utils/shared/operationColor'

export const ApiSubNavOperation = ({
  parentPath,
  operation,
  active = false,
  isSelected,
  setSelectedOperation,
  onClick,
}: {
  parentPath: string,
  operation: Operation,
  active?: boolean,
  isSelected: boolean,
  setSelectedOperation: (selectedOperation: string | null) => void,
  onClick?: (e) => void,
}) => {
  const { method, summary, path, deprecated } = operation
  const { hash: locationHash } = useLocation()
  const operationHash = buildOperationHash(operation)

  useEffect(() => {
    if (active && operationHash === locationHash) {
      setSelectedOperation(operation.operationId + operation.path + operation.method)
    } else if (!active) {
      setSelectedOperation(null)
    }
  }, [active, operationHash, locationHash, setSelectedOperation, operation.operationId])

  const navActiveItemRef = useScrollIntoView<HTMLAnchorElement>()

  const { isDark } = useDarkMode()

  const { closeSideNavWithoutTransition } = useOutletContext<RootOutletContext>()
  
  const onNavigate = (e) => {
    onClick?.(e)
    closeSideNavWithoutTransition?.()
  }

  return (
    <Link to={`${parentPath}${operationHash}`}
      onClick={onNavigate}
      className={clsx(
        'ml-1 grid grid-cols-[3.25rem_1fr] py-1.5 pl-2 pr-8 rounded max-w-full overflow-hidden z-40',
        active && 'hover:bg-neutral-20 dark:hover:bg-neutral-90',
        isSelected ? 'bg-neutral-20 dark:bg-neutral-90 text-consumer-blue dark:text-dark-consumer-blue font-semibold' : '',
        deprecated ? 'line-through text-neutral-50' : '',
      )} ref={(active && operationHash === locationHash) ? navActiveItemRef : null}>
      <div
        className={clsx(
          'font-semibold uppercase',
          deprecated && '!text-neutral-50 line-through'
        )}
        style={{ color: !deprecated ? getOperationColor(method, isDark()) : undefined }}
      >
        {method}
      </div>
      <div className="overflow-hidden break-words">
        {summary || path}
      </div>
    </Link>
  )
}